import classNames from "classnames";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  LuArrowLeft,
  LuCheck,
  LuMoreHorizontal,
  LuPencil,
  LuRefreshCw,
  LuSparkles,
  LuTrash2,
  LuUsers,
} from "react-icons/lu";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  AiReportCellKind,
  useAiReportInsightMutation,
  useAiReportSpecMutation,
} from "../../../slices/AiSlice";
import {
  CustomReport,
  useCreateCustomReportMutation,
  useDeleteCustomReportMutation,
  useGetCustomReportQuery,
  useUpdateCustomReportMutation,
} from "../../../slices/CustomReportsSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { aiErrorMessage } from "../../ai/ai.utils";
import { useAiStatus } from "../../ai/useAiStatus";
import { askTable } from "../ask/reportAsk.utils";
import { NameDialog } from "../saved/SavedReportControls";
import {
  ChoiceMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LoadError,
  Loading,
  NoAccess,
  Note,
  NUM,
  ReportPage,
  TableBox,
  TD,
  TH,
} from "../ui/ReportUi";
import { ChartLegend, ReportChart, SERIES_COLORS } from "./ReportChart";
import { formatChange, formatValue, ReportResult } from "./reportEngine";
import {
  Compare,
  COMPARE_LABELS,
  describeSpec,
  GROUP_LABELS,
  isTimeBucket,
  metricLabel,
  needsBilling,
  normalizeSpec,
  normalizeView,
  RANGE_LABELS,
  RangePreset,
  ReportSpec,
  ReportViewFilters,
} from "./reportSpec";
import { useCustomReportData } from "./useCustomReportData";

const BTN =
  "inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#DCE2EA] bg-white px-4 text-[13px] font-bold text-[#1F2433] hover:border-[#6B4EFF] disabled:opacity-50";
const BTN_AI =
  "inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#D9D1FF] bg-[#F5F3FF] px-4 text-[13px] font-bold text-[#6B4EFF] hover:bg-[#ECE7FF] disabled:opacity-50";
const BTN_PRIMARY =
  "inline-flex h-10 items-center gap-2 rounded-[10px] bg-[#6B4EFF] px-4 text-[13px] font-bold text-white hover:bg-[#5A3EE6] disabled:opacity-50";

const TAG =
  "inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-bold";

/** Draft handed over by "Create a report with AI" (not saved yet). */
export type CustomReportDraft = { spec: ReportSpec; request: string };

const askKind = (kind: string): AiReportCellKind =>
  kind === "money" || kind === "hours" || kind === "percent" ? kind : "number";

/** The page of one AI-created report: saved (`/reports/custom/:id`) or a new draft (`/reports/custom/new`). */
export const CustomReportPage: FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";
  const isNew = !id || id === "new";
  const draft = (location.state as { draft?: CustomReportDraft } | null)?.draft;
  const saved = useGetCustomReportQuery(
    { id: id || "", orgId },
    { skip: isNew || !orgId },
  );

  if (isNew && !draft) {
    return <Navigateback />;
  }
  if (!isNew && saved.isLoading) return <Loading />;
  if (!isNew && (saved.isError || !saved.data))
    return (
      <Shell>
        <NoAccess
          text={
            "This report doesn't exist any more, or it isn't shared with you."
          }
        />
      </Shell>
    );
  return (
    <ReportView
      key={id || "new"}
      saved={isNew ? null : (saved.data as CustomReport)}
      draft={isNew ? draft ?? null : null}
      onSaved={(report) =>
        navigate(`/reports/custom/${report.id}`, { replace: true })
      }
      onDeleted={() => navigate("/reports")}
    />
  );
};

const Shell: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className={
      "min-h-full bg-[#F6F7FB] px-4 py-8 font-Manrope text-[#1F2433] sm:px-8"
    }>
    <div className={"mx-auto max-w-[1240px]"}>
      <Link to={"/reports"} className={"text-[13px] font-bold text-[#6B4EFF]"}>
        ← All reports
      </Link>
      <div className={"mt-4"}>{children}</div>
    </div>
  </div>
);

const Navigateback: FC = () => {
  const navigate = useNavigate();
  useEffect(() => navigate("/reports", { replace: true }), [navigate]);
  return null;
};

const RANGE_OPTIONS = (Object.keys(RANGE_LABELS) as RangePreset[]).map(
  (value) => ({
    value,
    label: RANGE_LABELS[value],
  }),
);
const COMPARE_OPTIONS = (Object.keys(COMPARE_LABELS) as Compare[]).map(
  (value) => ({
    value,
    label: COMPARE_LABELS[value],
  }),
);

const ReportView: FC<{
  saved: CustomReport | null;
  draft: CustomReportDraft | null;
  onSaved: (report: CustomReport) => void;
  onDeleted: () => void;
}> = ({ saved, draft, onSaved, onDeleted }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { isOn: aiOn } = useAiStatus();
  const initial = useMemo(
    () => (saved ? normalizeSpec(saved.spec) : draft?.spec ?? null),
    [saved, draft],
  );
  const [spec, setSpec] = useState<ReportSpec | null>(initial);
  const [request, setRequest] = useState(
    saved?.request || draft?.request || "",
  );
  const [insight, setInsight] = useState<{
    text: string;
    stale: boolean;
  } | null>(saved?.insight ? { text: saved.insight, stale: false } : null);
  const [message, setMessage] = useState<{
    tone: "ok" | "error";
    text: string;
  } | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const savedView = useMemo(() => normalizeView(saved?.spec), [saved]);
  const state = useReportFilters(undefined, savedView);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameError, setRenameError] = useState("");
  const { result, isLoading, isError, access } = useCustomReportData(
    spec,
    state.filters,
  );
  const [makeSpec, specCall] = useAiReportSpecMutation();
  const [makeInsight, insightCall] = useAiReportInsightMutation();
  const [create, createCall] = useCreateCustomReportMutation();
  const [update, updateCall] = useUpdateCustomReportMutation();
  const [remove] = useDeleteCustomReportMutation();

  const mine = !saved || saved.mine;
  const view: ReportViewFilters = {
    userIds: state.filters.userIds,
    clientIds: state.filters.clientIds,
    workTypeIds: state.filters.workTypeIds,
  };
  const viewEdited =
    !!saved && JSON.stringify(savedView) !== JSON.stringify(view);
  const changed =
    !!saved &&
    (JSON.stringify(normalizeSpec(saved.spec)) !== JSON.stringify(spec) ||
      viewEdited);

  // A new draft gets its "What stands out" once the numbers are in (1 AI use).
  const askedInsight = useRef(false);
  const refreshInsight = async (r: ReportResult, s: ReportSpec) => {
    try {
      const res = await makeInsight({
        orgId,
        title: s.title,
        request,
        filters: `${RANGE_LABELS[s.range]}${
          s.compare !== "none"
            ? ` vs ${COMPARE_LABELS[s.compare].toLowerCase()}`
            : ""
        }`,
        columns: [
          { key: "label", label: GROUP_LABELS[s.groupBy], kind: "text" },
          ...r.columns.map((c) => ({
            key: c.key,
            label: c.label,
            kind: askKind(c.kind),
          })),
        ],
        rows: r.rows
          .slice(0, 100)
          .map((row) => ({ label: row.label, cells: row.values })),
        totals: r.totals,
      }).unwrap();
      setInsight({ text: res.text, stale: false });
    } catch (error) {
      setMessage({ tone: "error", text: aiErrorMessage(error) });
    }
  };
  useEffect(() => {
    if (
      !saved &&
      result &&
      spec &&
      aiOn &&
      !askedInsight.current &&
      result.rows.length
    ) {
      askedInsight.current = true;
      refreshInsight(result, spec);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, aiOn]);

  const { pageRows, pager } = usePagedRows(result?.rows ?? []);

  const askData = useMemo(() => {
    if (!result || !spec) return undefined;
    return {
      title: spec.title,
      path: saved ? `/reports/custom/${saved.id}` : "/reports/custom/new",
      description: spec.description,
      filters: `${RANGE_LABELS[spec.range]}${
        spec.compare !== "none"
          ? ` vs ${COMPARE_LABELS[spec.compare].toLowerCase()}`
          : ""
      }`,
      kpis: spec.metrics.map((m) => ({
        label: metricLabel(m, spec.source),
        value: formatValue(
          result.columns.find((c) => c.key === m)?.kind ?? "count",
          result.totals[m],
        ),
      })),
      tables: [
        askTable(
          spec.title,
          result.rows,
          [
            {
              key: "label",
              label: GROUP_LABELS[spec.groupBy],
              kind: "text" as const,
              value: (r) => r.label,
            },
            ...result.columns.map((c) => ({
              key: c.key,
              label: c.label,
              kind: askKind(c.kind),
              value: (r: typeof result.rows[number]) => r.values[c.key],
            })),
          ],
          (r) => r.key,
          (r) => r.link,
        ),
      ],
    };
  }, [result, spec, saved]);

  if (!spec)
    return (
      <Shell>
        <NoAccess text={"This report can't be shown. Try creating it again."} />
      </Shell>
    );
  if (needsBilling(spec) && !access.billing)
    return (
      <Shell>
        <NoAccess
          text={
            "This report shows billing amounts, which are only for Admins and people with View Billing permission."
          }
        />
      </Shell>
    );

  const save = async (asCopy = false) => {
    setMessage(null);
    const body = {
      orgId,
      // A renamed report keeps its name when changes are saved.
      name: saved && saved.mine && !asCopy ? saved.name : spec.title,
      description: spec.description,
      request,
      spec: { ...spec, view },
      ...(insight && !insight.stale ? { insight: insight.text } : {}),
    };
    try {
      const report =
        saved && saved.mine && !asCopy
          ? await update({ id: saved.id, ...body }).unwrap()
          : await create({ ...body, shared: false }).unwrap();
      setMessage({
        tone: "ok",
        text: saved && !asCopy ? "Changes saved." : "Saved to My reports.",
      });
      if (!saved || asCopy) onSaved(report);
    } catch (error) {
      setMessage({ tone: "error", text: aiErrorMessage(error) });
    }
  };

  const rename = async (name: string) => {
    if (!saved) return;
    setRenameError("");
    try {
      await update({ id: saved.id, orgId, name }).unwrap();
      setRenameOpen(false);
    } catch (error) {
      setRenameError(aiErrorMessage(error));
    }
  };

  const toggleShare = async () => {
    if (!saved) return;
    try {
      await update({ id: saved.id, orgId, shared: !saved.shared }).unwrap();
      setMessage({
        tone: "ok",
        text: saved.shared
          ? "Only you can see this report now."
          : "Your team can now see this report in Reports.",
      });
    } catch (error) {
      setMessage({ tone: "error", text: aiErrorMessage(error) });
    }
  };

  const onDelete = async () => {
    if (
      !saved ||
      !window.confirm(`Delete “${saved.name}”? This can't be undone.`)
    )
      return;
    try {
      await remove({ id: saved.id, orgId }).unwrap();
      onDeleted();
    } catch (error) {
      setMessage({ tone: "error", text: aiErrorMessage(error) });
    }
  };

  const change = async (text: string) => {
    setMessage(null);
    try {
      const res = await makeSpec({
        orgId,
        request: text,
        current: spec,
      }).unwrap();
      const next = normalizeSpec(res.spec);
      if (!next) throw new Error("bad spec");
      setSpec(next);
      setRequest((r) => (r ? `${r} — then: ${text}` : text).slice(0, 1000));
      setInsight((i) => (i ? { ...i, stale: true } : i));
      setChangeOpen(false);
    } catch (error) {
      setMessage({ tone: "error", text: aiErrorMessage(error) });
    }
  };

  const setView = (patch: Partial<ReportSpec>) => {
    const next = normalizeSpec({ ...spec, ...patch });
    if (next) {
      setSpec(next);
      setInsight((i) => (i ? { ...i, stale: true } : i));
    }
  };

  const onExport = () => {
    if (!result) return;
    exportDataToExcel(
      [
        ...result.rows,
        { key: "total", label: "Total", values: result.totals, split: {} },
      ].map((row) => ({
        [GROUP_LABELS[spec.groupBy]]: row.label,
        ...Object.fromEntries(
          result.columns.map((c) => [
            c.label,
            row.values[c.key] === null
              ? ""
              : Math.round((row.values[c.key] as number) * 100) / 100,
          ]),
        ),
      })),
      spec.title,
    );
  };

  const first = result?.columns[0];
  const chartPoints =
    result && first
      ? result.splitKeys.length && spec.chart === "stacked_bar"
        ? result.rows.map((r) => ({
            key: r.key,
            label: r.label,
            values: result.splitKeys.map((k) => r.split[k.key] ?? 0),
          }))
        : result.rows.map((r) => ({
            key: r.key,
            label: r.label,
            values: result.previousLabel
              ? [r.values[first.key], r.values[`${first.key}__prev`]]
              : [r.values[first.key]],
          }))
      : [];
  const chartSeries =
    result && first
      ? result.splitKeys.length && spec.chart === "stacked_bar"
        ? result.splitKeys.map((k) => k.label)
        : result.previousLabel
        ? [first.label, result.previousLabel]
        : [first.label]
      : [];
  const chartColors =
    result?.previousLabel && spec.chart !== "stacked_bar"
      ? ["#6B4EFF", "#C9BEFF"]
      : SERIES_COLORS;

  const header = (
    <div className={"min-w-0"}>
      <div
        className={
          "flex items-center gap-1 text-[13px] font-semibold text-[#6B7185]"
        }>
        <Link
          to={"/reports"}
          className={
            "inline-flex items-center gap-1 font-bold text-[#6B4EFF] hover:text-[#5236E8]"
          }>
          <LuArrowLeft size={14} strokeWidth={2.4} />
          Reports
        </Link>
        <span>›</span>
        <span>My reports</span>
      </div>
      <div className={"mt-1.5 flex flex-wrap items-center gap-2.5"}>
        <h1 className={"text-[26px] font-extrabold leading-tight"}>
          {saved?.name || spec.title}
        </h1>
        <span className={classNames(TAG, "bg-[#F1EEFF] text-[#6B4EFF]")}>
          <LuSparkles size={11} /> Created with AI
        </span>
        {saved?.shared && (
          <span className={classNames(TAG, "bg-[#ECFDF3] text-[#12805C]")}>
            <LuUsers size={11} /> Shared with team
          </span>
        )}
        {!saved && (
          <span className={classNames(TAG, "bg-[#FFF4E5] text-[#B45309]")}>
            Not saved yet
          </span>
        )}
        {changed && mine && (
          <span className={classNames(TAG, "bg-[#FFF4E5] text-[#B45309]")}>
            Unsaved changes
          </span>
        )}
      </div>
      {spec.description && (
        <p className={"mt-1 max-w-[760px] text-sm text-[#5B6472]"}>
          {spec.description}
        </p>
      )}
      {request && (
        <p className={"mt-1 max-w-[900px] text-[12.5px] text-[#6B7185]"}>
          Your request:{" "}
          <b className={"font-semibold text-[#3A4256]"}>“{request}”</b>
        </p>
      )}
      <p className={"mt-0.5 max-w-[900px] text-[12.5px] text-[#6B7185]"}>
        How it&apos;s calculated: {describeSpec(spec)}
      </p>
      {saved && !saved.mine && saved.createdByName && (
        <p className={"mt-0.5 text-[12.5px] text-[#6B7185]"}>
          Shared by {saved.createdByName}
        </p>
      )}
    </div>
  );

  const actions = (
    <>
      {aiOn && (
        <div className={"relative"}>
          <button
            type={"button"}
            className={BTN_AI}
            onClick={() => setChangeOpen((o) => !o)}>
            <LuSparkles size={15} /> Change with AI
          </button>
          {changeOpen && (
            <ChangeBox
              busy={specCall.isLoading}
              onClose={() => setChangeOpen(false)}
              onSubmit={change}
            />
          )}
        </div>
      )}
      {mine ? (
        (!saved ||
          changed ||
          (insight && !insight.stale && insight.text !== saved.insight)) && (
          <button
            type={"button"}
            className={BTN_PRIMARY}
            disabled={createCall.isLoading || updateCall.isLoading}
            onClick={() => save()}>
            <LuCheck size={15} /> {saved ? "Save changes" : "Save report"}
          </button>
        )
      ) : (
        <button
          type={"button"}
          className={BTN}
          disabled={createCall.isLoading}
          onClick={() => save(true)}>
          Save a copy
        </button>
      )}
      {saved && saved.mine && (
        <button
          type={"button"}
          className={BTN}
          onClick={toggleShare}
          disabled={updateCall.isLoading}>
          <LuUsers size={15} />{" "}
          {saved.shared ? "Stop sharing" : "Share with team"}
        </button>
      )}
      {saved && (saved.mine || access.isAdmin) && (
        <div className={"relative"}>
          <button
            type={"button"}
            aria-label={"More"}
            className={classNames(BTN, "px-3")}
            onClick={() => setMenuOpen((o) => !o)}>
            <LuMoreHorizontal size={16} />
          </button>
          {menuOpen && (
            <div
              className={
                "absolute right-0 top-[46px] z-30 min-w-[170px] rounded-xl border border-[#EDEFF3] bg-white p-1.5 shadow-[0_12px_32px_rgba(20,22,34,0.14)]"
              }>
              {saved.mine && (
                <button
                  type={"button"}
                  onClick={() => {
                    setMenuOpen(false);
                    setRenameOpen(true);
                  }}
                  className={
                    "flex w-full items-center gap-2 rounded-[7px] px-2.5 py-2 text-left text-[13px] font-semibold text-[#3A4256] hover:bg-[#F5F6F8]"
                  }>
                  <LuPencil size={14} /> Rename
                </button>
              )}
              <button
                type={"button"}
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
                className={
                  "flex w-full items-center gap-2 rounded-[7px] px-2.5 py-2 text-left text-[13px] font-semibold text-[#B42318] hover:bg-[#FEF3F2]"
                }>
                <LuTrash2 size={14} /> Delete report
              </button>
            </div>
          )}
        </div>
      )}
      {renameOpen && saved && (
        <NameDialog
          title={"Rename report"}
          initialName={saved.name}
          saveLabel={"Rename"}
          busy={updateCall.isLoading}
          error={renameError}
          onSave={rename}
          onCancel={() => {
            setRenameOpen(false);
            setRenameError("");
          }}
        />
      )}
    </>
  );

  return (
    <ReportPage
      title={spec.title}
      description={spec.description}
      header={header}
      actions={actions}
      onExport={result?.rows.length ? onExport : undefined}
      ask={askData}
      askSuggestions={[
        "What stands out?",
        "Which is the biggest and the smallest?",
        "What changed the most?",
      ]}
      filters={
        <>
          <ChoiceMenu
            label={"Date"}
            options={RANGE_OPTIONS}
            value={spec.range}
            onChange={(range) => setView({ range })}
          />
          {spec.range !== "all_time" && (
            <ChoiceMenu
              label={"Compare to"}
              options={COMPARE_OPTIONS}
              value={spec.compare}
              onChange={(compare) => setView({ compare })}
            />
          )}
          <PeopleClientTypeFilters
            state={state}
            showPeople={access.firmWide}
            showWorkTypes={spec.source !== "invoices"}
          />
        </>
      }>
      {message && (
        <div
          role={message.tone === "error" ? "alert" : "status"}
          className={classNames(
            "rounded-xl px-3.5 py-2.5 text-[13px] font-semibold",
            message.tone === "error"
              ? "border border-[#FAD4D0] bg-[#FEF3F2] text-[#B42318]"
              : "bg-[#ECFDF3] text-[#12805C]",
          )}>
          {message.text}
        </div>
      )}
      {isError && <LoadError />}
      {result && (
        <KpiGrid>
          {spec.metrics.map((m) => {
            const kind =
              result.columns.find((c) => c.key === m)?.kind ?? "count";
            const delta = result.totals[`${m}__change`];
            return (
              <Kpi
                key={m}
                label={metricLabel(m, spec.source)}
                value={formatValue(kind, result.totals[m])}
                note={
                  delta !== undefined ? (
                    <span
                      className={classNames(
                        "font-bold",
                        (delta ?? 0) >= 0 ? "text-[#12805C]" : "text-[#B42318]",
                      )}>
                      {formatChange(kind, delta)} vs{" "}
                      {result.previousLabel?.toLowerCase()}
                    </span>
                  ) : undefined
                }
              />
            );
          })}
        </KpiGrid>
      )}
      {aiOn &&
        result &&
        result.rows.length > 0 &&
        (insight || insightCall.isLoading) && (
          <div
            className={
              "rounded-[14px] border border-[#E6E0FF] bg-[#FBFAFF] px-4 py-3.5 text-[13.5px] leading-[1.55]"
            }
            data-testid={"report-insight"}>
            <div className={"mb-1 flex items-center gap-2"}>
              <b className={"inline-flex items-center gap-1.5 text-[#6B4EFF]"}>
                <LuSparkles size={14} /> What stands out
              </b>
              {insight?.stale && (
                <span className={"text-[12px] font-semibold text-[#B45309]"}>
                  Written for an earlier version of this report
                </span>
              )}
              <button
                type={"button"}
                disabled={insightCall.isLoading}
                onClick={() => result && refreshInsight(result, spec)}
                className={
                  "ml-auto inline-flex items-center gap-1 text-[12.5px] font-bold text-[#6B4EFF] hover:underline disabled:opacity-50"
                }>
                <LuRefreshCw size={13} /> Refresh
              </button>
            </div>
            {insightCall.isLoading ? (
              <span className={"animate-pulse text-[#6B7185]"}>
                Reading the report…
              </span>
            ) : (
              <span className={"whitespace-pre-line text-[#1F2433]"}>
                {insight?.text}
              </span>
            )}
          </div>
        )}
      {aiOn &&
        result &&
        result.rows.length > 0 &&
        !insight &&
        !insightCall.isLoading && (
          <button
            type={"button"}
            onClick={() => refreshInsight(result, spec)}
            className={classNames(BTN_AI, "self-start")}>
            <LuSparkles size={15} /> What stands out?
          </button>
        )}
      {isLoading && !isError && <Loading />}
      {result && spec.chart !== "none" && result.rows.length > 0 && (
        <Card
          title={
            first
              ? spec.chart === "stacked_bar" && result.splitKeys.length
                ? `${first.label} by ${
                    spec.splitBy === "billable"
                      ? "billable / non-billable"
                      : spec.splitBy
                  }`
                : first.label
              : ""
          }
          right={<ChartLegend series={chartSeries} colors={chartColors} />}>
          <ReportChart
            kind={
              spec.chart === "stacked_bar" && !result.splitKeys.length
                ? "bar"
                : (spec.chart as "bar" | "stacked_bar" | "line")
            }
            series={chartSeries}
            points={chartPoints.slice(0, isTimeBucket(spec.groupBy) ? 60 : 25)}
            format={(v) => formatValue(first?.kind ?? "count", v)}
            colors={chartColors}
          />
        </Card>
      )}
      {result && (
        <Card title={`By ${GROUP_LABELS[spec.groupBy].toLowerCase()}`}>
          {!result.rows.length ? (
            <Empty
              text={"Nothing matches this report for these dates and filters."}
            />
          ) : (
            <TableBox pager={pager}>
              <thead>
                <tr>
                  <th scope={"col"} className={TH}>
                    {GROUP_LABELS[spec.groupBy]}
                  </th>
                  {result.columns.map((c) => (
                    <th key={c.key} scope={"col"} className={`${TH} ${NUM}`}>
                      {c.label}
                    </th>
                  ))}
                  {result.splitKeys.map((k) => (
                    <th
                      key={`split-${k.key}`}
                      scope={"col"}
                      className={`${TH} ${NUM}`}>
                      {k.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.key}>
                    <td className={`${TD} font-bold`}>
                      {row.link ? (
                        <Link
                          to={row.link}
                          className={
                            "font-bold text-[#6B4EFF] no-underline hover:underline"
                          }>
                          {row.label}
                        </Link>
                      ) : (
                        row.label
                      )}
                    </td>
                    {result.columns.map((c) => {
                      const v = row.values[c.key];
                      return (
                        <td
                          key={c.key}
                          className={classNames(
                            TD,
                            NUM,
                            c.role === "value" && "font-bold",
                            c.role === "previous" && "text-[#6B7185]",
                            c.role === "change" && "font-bold",
                          )}
                          style={
                            c.role === "change" && v !== null && v !== 0
                              ? { color: v > 0 ? "#12805C" : "#B42318" }
                              : undefined
                          }>
                          {c.role === "change"
                            ? formatChange(c.kind, v)
                            : formatValue(c.kind, v)}
                        </td>
                      );
                    })}
                    {result.splitKeys.map((k) => (
                      <td
                        key={`split-${k.key}`}
                        className={`${TD} ${NUM} text-[#3A4256]`}>
                        {formatValue(
                          first?.kind ?? "count",
                          row.split[k.key] ?? 0,
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className={`${TD} font-extrabold`}>Total</td>
                  {result.columns.map((c) => (
                    <td key={c.key} className={`${TD} ${NUM} font-extrabold`}>
                      {c.role === "change"
                        ? formatChange(c.kind, result.totals[c.key])
                        : formatValue(c.kind, result.totals[c.key])}
                    </td>
                  ))}
                  {result.splitKeys.map((k) => (
                    <td
                      key={`split-${k.key}`}
                      className={`${TD} ${NUM} font-extrabold`}>
                      {formatValue(
                        first?.kind ?? "count",
                        result.rows.reduce(
                          (sum, r) => sum + (r.split[k.key] ?? 0),
                          0,
                        ),
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </TableBox>
          )}
          {result.hiddenRows > 0 && (
            <div className={"mt-2 text-[12.5px] text-[#6B7185]"}>
              {result.hiddenRows} more{" "}
              {result.hiddenRows === 1 ? "row" : "rows"} not shown (top{" "}
              {spec.limit}); the totals include them.
            </div>
          )}
        </Card>
      )}
      <Note>
        ProCharted calculates every number from your firm&apos;s data; the AI
        only chose what to show. You see only the work, time and billing your
        access allows.
      </Note>
    </ReportPage>
  );
};

/** "Change with AI": one line describing the change. */
const ChangeBox: FC<{
  busy: boolean;
  onClose: () => void;
  onSubmit: (text: string) => void;
}> = ({ busy, onClose, onSubmit }) => {
  const [text, setText] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) onSubmit(text.trim());
      }}
      className={
        "absolute right-0 top-[46px] z-30 w-[360px] rounded-[14px] border border-[#E4E7EC] bg-white p-3 shadow-[0_12px_32px_rgba(16,24,40,0.14)]"
      }>
      <div className={"mb-1.5 text-[13px] font-extrabold"}>
        What should change?
      </div>
      <textarea
        autoFocus
        rows={2}
        maxLength={1000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (text.trim()) onSubmit(text.trim());
          }
        }}
        placeholder={"e.g. compare with last year, or show it per month"}
        className={
          "w-full resize-none rounded-[10px] border border-[#DCE2EA] px-3 py-2 text-[13.5px] outline-none focus:border-[#6B4EFF]"
        }
      />
      <div className={"mt-2 flex justify-end gap-2"}>
        <button
          type={"button"}
          onClick={onClose}
          className={
            "h-9 rounded-[9px] px-3 text-[13px] font-bold text-[#3A4256]"
          }>
          Cancel
        </button>
        <button
          type={"submit"}
          disabled={busy || !text.trim()}
          className={
            "inline-flex h-9 items-center gap-1.5 rounded-[9px] bg-[#6B4EFF] px-3.5 text-[13px] font-bold text-white disabled:opacity-50"
          }>
          <LuSparkles size={14} /> {busy ? "Changing…" : "Change report"}
        </button>
      </div>
    </form>
  );
};
